/**
 * The engineer's own trail queue (admins see all).
 *
 * Layout follows AdminSettingsPage -- same shell, same paddings, same header
 * treatment -- so it reads as part of the admin area rather than a page that
 * landed next to it.
 *
 * IMPORTANT: mounting the dashboard's <SideBar> here crashes the page (blank
 * screen, React error boundary confirmed live). Do NOT import it. Agents get
 * a self-contained left rail instead.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw,
  AlertTriangle,
  GraduationCap,
  Search,
  X,
  Trash2,
  Sparkles,
  WifiOff,
  Loader2,
} from "lucide-react";
import AdminSideBar from "../AdminSideBar";
import LearningAgentSideBar from "./LearningAgentSideBar";
import UserProfileMenu from "../UserProfileMenu";
import SelectMenu from "../dashboard5/SelectMenu";
import { getStoredRole } from "../../utils/auth";
import { listTrails, discardTrail } from "./useLearning";
import { modeLabel, modesLabel } from "./modeLabels";

const AGENT_ROLES = new Set(["agent", "support_agent"]);

const TABS = [
  { key: "draft", label: "Draft" },
  { key: "submitted", label: "Submitted" },
  { key: "published", label: "Published" },
  { key: "discarded", label: "Discarded" },
];

const EMPTY_COPY = {
  draft:
    "No drafts. Trails appear here when a conversation ends — review and submit them.",
  submitted: "Nothing waiting for approval.",
  published: "No trails published yet.",
  discarded: "Nothing discarded.",
};

// `owner` is the column the backend fills; `created_by` is the older name that
// still turns up on trails captured before the rename.
const rowOwner = (r) => r.owner || r.created_by || "";

// Strip leading boilerplate so the actual symptom detail leads in the table.
// Only display-side — stored data is never mutated.
const SYMPTOM_PREFIXES = /^(symptom confirmed|confirmed symptom)\s*:\s*/i;
const displaySymptom = (text) => {
  if (!text) return "";
  return text.replace(SYMPTOM_PREFIXES, "").replace(/^\s+/, "");
};

// Format a date string to match the house style used in AdminPage.
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return "—";
  }
};

const MyTrails = ({ onLogout }) => {
  const navigate = useNavigate();
  const role = getStoredRole();
  const isAdmin = role === "admin";
  const isAgent = AGENT_ROLES.has(role);
  const [tab, setTab] = useState("draft");
  const [rows, setRows] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // True when the load failed due to a backend error (503) — visually distinct
  // from "no trails yet" so an admin never confuses "broken" with "empty".
  const [loadFailed, setLoadFailed] = useState(false);
  // Badge count for the Submitted tab (admin inbox)
  const [submittedCount, setSubmittedCount] = useState(0);
  // Server-side search: the raw input and the debounced value sent to the API.
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  // Client-side filters over the rows already fetched for this tab.
  const [product, setProduct] = useState("");
  const [owner, setOwner] = useState("");
  const [mode, setMode] = useState("");
  const [needsMarking, setNeedsMarking] = useState(false);

  // Debounce the search query — 350ms so we do not fire per keystroke,
  // and cancel stale timers on each change.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => clearTimeout(timer);
  }, [query]);

  // Guard against out-of-order responses: each load increments a counter and
  // ignores responses from older requests.
  const loadSeqRef = useRef(0);

  // Discard confirmation state: { id, symptom } of the trail being confirmed,
  // or null when the dialog is closed.
  const [confirmDiscard, setConfirmDiscard] = useState(null);
  const [discardBusy, setDiscardBusy] = useState(false);

  // Whether the current user may discard a given row. Admin: any non-discarded
  // trail. Non-admin owner: own drafts only. Everyone else: never.
  const canDiscard = useCallback(
    (row) => {
      if (row.status === "discarded" || tab === "discarded") return false;
      if (isAdmin) return true;
      // Non-admin agents can only discard their own drafts.
      if (!isAgent) return false;
      if ((row.status || tab) !== "draft") return false;
      const me =
        localStorage.getItem("email") ||
        localStorage.getItem("username") ||
        "";
      return me && rowOwner(row) === me;
    },
    [isAdmin, isAgent, tab]
  );

  const handleDiscard = useCallback(
    async (trail) => {
      setDiscardBusy(true);
      setError("");
      // Optimistically remove the row.
      const prevRows = rows;
      setRows((r) => r.filter((x) => x.id !== trail.id));
      try {
        await discardTrail(trail.id);
        setConfirmDiscard(null);
      } catch (e) {
        // Rollback and surface the error.
        setRows(prevRows);
        setError(e.message || "Could not discard trail.");
      } finally {
        setDiscardBusy(false);
      }
    },
    [rows]
  );

  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    setError("");
    setLoadFailed(false);
    try {
      const data = await listTrails(tab, {
        search: debouncedQuery || undefined,
      });
      // Ignore stale responses — a newer request is already in flight.
      if (seq !== loadSeqRef.current) return;
      if (data === null) {
        setDisabled(true);
        setRows([]);
      } else {
        setDisabled(false);
        setRows(data.trails || []);
      }
    } catch (e) {
      if (seq !== loadSeqRef.current) return;
      setLoadFailed(true);
      setError(e.message || "Could not load trails.");
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [tab, debouncedQuery]);

  // Fetch submitted count for the admin badge (independent of active tab)
  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await listTrails("submitted");
        if (!cancelled && data) {
          setSubmittedCount(data.count ?? (data.trails || []).length);
        }
      } catch {
        /* badge is never worth surfacing an error */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  /* ---- filters ---------------------------------------------------- */

  const clearFilters = useCallback(() => {
    setQuery("");
    setDebouncedQuery("");
    setProduct("");
    setOwner("");
    setMode("");
    setNeedsMarking(false);
  }, []);

  // A filter armed on the previous tab would silently hide rows here — and a
  // product that existed in Draft may not exist in Published at all.
  useEffect(() => {
    clearFilters();
  }, [tab, clearFilters]);

  // Options come from the rows actually on screen, so we never offer a filter
  // that can only return nothing.
  const products = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => {
      if (r.product) seen.add(r.product);
    });
    return [...seen].sort();
  }, [rows]);

  const owners = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => {
      const o = rowOwner(r);
      if (o) seen.add(o);
    });
    return [...seen].sort();
  }, [rows]);

  // `modes` is an array per row ("guided", "fast", …); the pills filter on
  // membership rather than on the joined string, so "guided" also matches a
  // trail that ran fast + guided.
  const modes = useMemo(() => {
    const seen = new Set();
    rows.forEach((r) => {
      (r.modes || []).forEach((m) => {
        if (m) seen.add(m);
      });
    });
    return [...seen].sort();
  }, [rows]);

  const hasUnmarked = useMemo(
    () => rows.some((r) => Number(r.unmarked) > 0),
    [rows]
  );

  const filtersActive =
    !!query.trim() || !!product || !!owner || !!mode || needsMarking;

  // Search is now server-side; client-side filters (product/owner/mode/marking)
  // still apply on top of the server result.
  const visibleRows = useMemo(() => {
    return rows.filter((r) => {
      if (product && r.product !== product) return false;
      if (owner && rowOwner(r) !== owner) return false;
      if (mode && !(r.modes || []).includes(mode)) return false;
      if (needsMarking && !(Number(r.unmarked) > 0)) return false;
      return true;
    });
  }, [rows, product, owner, mode, needsMarking]);

  const pageTitle = isAdmin ? "Learning Trails" : "My Learning Trails";
  const pageDesc = isAdmin
    ? "All trails across the team. Approve submitted trails to publish them."
    : "Trails from your sessions — review, refine and submit them for approval.";

  return (
    <div className="flex h-screen w-full bg-[#FAFAF6] overflow-hidden">
      {/* Agents get a self-contained rail. Admins get AdminSideBar. */}
      {!isAgent ? <AdminSideBar onLogout={onLogout} /> : <LearningAgentSideBar onLogout={onLogout} />}

      <div className="flex-1 relative flex flex-col px-6 sm:px-10 lg:px-16 xl:px-24 py-8 overflow-y-auto overflow-x-hidden min-w-0">
        <div className="absolute top-4 right-6 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>

        <div className="w-full min-w-0">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-[#253439]">{pageTitle}</h1>
              <p className="text-gray-500 text-sm mt-1">{pageDesc}</p>
            </div>
            <button
              onClick={load}
              disabled={loading}
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition disabled:opacity-50"
            >
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>


          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {disabled ? (
            <div className="border border-[#EBECE6] rounded-xl bg-white shadow-sm px-6 py-12 text-center">
              <GraduationCap size={26} className="mx-auto mb-3 text-gray-300" />
              <p className="text-[#253439] font-semibold">
                The learning module is switched off
              </p>
              <p className="text-gray-500 text-sm mt-1">
                Turn it on in{" "}
                <button
                  type="button"
                  onClick={() => navigate("/admin/settings")}
                  className="text-cyan-600 hover:underline font-medium"
                >
                  Settings
                </button>{" "}
                to start capturing how conversations were diagnosed.
              </p>
            </div>
          ) : (
            <>
              <div className="flex gap-2 mb-4">
                {TABS.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTab(t.key)}
                    className={`px-3.5 py-1.5 rounded-full text-sm transition border flex items-center gap-1.5 ${
                      tab === t.key
                        ? "bg-[#253439] border-[#253439] text-white font-semibold"
                        : "bg-white border-[#EBECE6] text-gray-500 hover:border-gray-300 hover:text-[#253439]"
                    }`}
                  >
                    {t.label}
                    {/* Admin inbox badge on the Submitted tab */}
                    {t.key === "submitted" && isAdmin && submittedCount > 0 && (
                      <span
                        className={`inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[11px] font-bold leading-none px-1 ${
                          tab === "submitted"
                            ? "bg-white text-[#253439]"
                            : "bg-amber-500 text-white"
                        }`}
                      >
                        {submittedCount}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Search + filters — one aligned toolbar.
                  Search is server-side (debounced); client-side filters
                  (product/owner/mode) apply on top. Always mounted so the
                  search box keeps focus while loading. */}
              {!loadFailed && (
                <div className="flex flex-wrap items-center gap-2 mb-4 min-w-0">
                  <div className="relative w-56">
                    <Search
                      size={14}
                      aria-hidden="true"
                      className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-300"
                    />
                    <input
                      type="text"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Search trails..."
                      aria-label="Search trails"
                      className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-9 pr-8 text-[13px] text-gray-700 placeholder-gray-400 outline-none transition-colors focus:border-cyan-300"
                    />
                    {query && (
                      <button
                        type="button"
                        onClick={() => setQuery("")}
                        aria-label="Clear search"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 transition-colors hover:text-cyan-700"
                      >
                        <X size={13} />
                      </button>
                    )}
                  </div>

                  {modes.length > 1 && (
                    <>
                      <span className="mx-1 h-5 w-px bg-gray-200" aria-hidden="true" />
                      <button
                        type="button"
                        onClick={() => setMode("")}
                        aria-pressed={mode === ""}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                          mode === ""
                            ? "bg-cyan-500 text-white"
                            : "bg-gray-100 text-gray-500 hover:bg-cyan-50 hover:text-cyan-700"
                        }`}
                      >
                        All modes
                      </button>
                      {modes.map((m) => (
                        <button
                          key={m}
                          type="button"
                          onClick={() => setMode(m)}
                          aria-pressed={mode === m}
                          className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                            mode === m
                              ? "bg-cyan-500 text-white"
                              : "bg-gray-100 text-gray-500 hover:bg-cyan-50 hover:text-cyan-700"
                          }`}
                        >
                          {modeLabel(m)}
                        </button>
                      ))}
                    </>
                  )}
                  {hasUnmarked && (
                    <button
                      type="button"
                      onClick={() => setNeedsMarking((v) => !v)}
                      aria-pressed={needsMarking}
                      className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors ${
                        needsMarking
                          ? "bg-cyan-500 text-white"
                          : "bg-gray-100 text-gray-500 hover:bg-cyan-50 hover:text-cyan-700"
                      }`}
                    >
                      Needs marking
                    </button>
                  )}
                  {filtersActive && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium text-gray-400 transition-colors hover:text-cyan-700"
                    >
                      <X size={11} />
                      Clear
                    </button>
                  )}

                  <div className="ml-auto flex flex-wrap items-center gap-1.5">
                    {products.length > 1 && (
                      <SelectMenu
                        value={product}
                        onChange={setProduct}
                        options={[
                          { value: "", label: "All products" },
                          ...products.map((p) => ({ value: p, label: p })),
                        ]}
                        ariaLabel="Filter by product"
                      />
                    )}
                    {/* Owner only means something to an admin — everyone else
                        is already looking at nothing but their own trails. */}
                    {isAdmin && owners.length > 1 && (
                      <SelectMenu
                        value={owner}
                        onChange={setOwner}
                        options={[
                          { value: "", label: "All owners" },
                          ...owners.map((o) => ({ value: o, label: o })),
                        ]}
                        ariaLabel="Filter by owner"
                      />
                    )}
                  </div>
                </div>
              )}

              <div className="border border-[#EBECE6] rounded-xl overflow-hidden bg-white shadow-sm relative">
                {/* Subtle overlay when reloading with existing rows visible */}
                {loading && rows.length > 0 && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 rounded-xl">
                    <Loader2 size={22} className="animate-spin text-cyan-500" />
                  </div>
                )}
                {loading && rows.length === 0 ? (
                  <div className="flex items-center justify-center py-12 text-gray-400">
                    <Loader2 size={22} className="animate-spin" />
                  </div>
                ) : loadFailed ? (
                  <div className="text-center py-12">
                    <WifiOff size={26} className="mx-auto mb-3 text-red-300" />
                    <p className="text-[#253439] font-semibold">
                      Could not load trails
                    </p>
                    <p className="text-gray-500 text-sm mt-1 max-w-md mx-auto">
                      {error || "The server did not respond. This is usually temporary."}
                    </p>
                    <button
                      type="button"
                      onClick={load}
                      className="mt-4 inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
                    >
                      <RefreshCw size={14} />
                      Retry
                    </button>
                  </div>
                ) : rows.length === 0 ? (
                  <div className="text-center py-12 text-gray-400 text-sm">
                    {debouncedQuery
                      ? "No trails match that search."
                      : EMPTY_COPY[tab] || "Nothing here."}
                  </div>
                ) : visibleRows.length === 0 ? (
                  <div className="text-center py-12 text-sm">
                    <p className="text-gray-400">No trails match that search.</p>
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="mt-2 text-cyan-600 hover:underline font-medium text-[13px]"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[820px]">
                      <thead>
                        <tr className="bg-[#f6f6f1] text-left text-gray-600 sticky top-0 z-[5]">
                          <th className="px-4 py-3 font-semibold">Symptom</th>
                          {isAdmin && (
                            <th className="px-4 py-3 font-semibold w-28">
                              Owner
                            </th>
                          )}
                          <th className="px-4 py-3 font-semibold w-24">
                            Product
                          </th>
                          <th className="px-4 py-3 font-semibold w-36">Mode</th>
                          <th className="px-4 py-3 font-semibold w-20 text-right">
                            Steps
                          </th>
                          {/* Status column only when tab shows mixed statuses (discarded) */}
                          {tab === "discarded" && (
                            <th className="px-4 py-3 font-semibold w-28">
                              Status
                            </th>
                          )}
                          <th className="px-4 py-3 font-semibold w-36">
                            Created
                          </th>
                          {tab !== "discarded" && (
                            <th className="px-4 py-3 font-semibold w-10">
                              <span className="sr-only">Actions</span>
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {visibleRows.map((r) => {
                          const symptom = displaySymptom(r.symptom_text);
                          return (
                          <tr
                            key={r.id}
                            onClick={() => navigate(`/learning/${r.id}`)}
                            className="group border-t border-[#EBECE6] cursor-pointer hover:bg-[#FAFAF6] transition"
                            role="link"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                navigate(`/learning/${r.id}`);
                              }
                            }}
                          >
                            <td className="px-4 py-3 text-[#253439] max-w-xl" title={r.symptom_text}>
                              <span className="flex items-center gap-1.5 min-w-0">
                                <span className="line-clamp-2 min-w-0">
                                  {symptom}
                                </span>
                                {r.match_reason === "semantic" && (
                                  <span
                                    title="Semantic match — similar meaning, not exact words"
                                    className="inline-flex items-center gap-0.5 rounded-full bg-purple-50 px-1.5 py-0.5 text-[10px] font-medium text-purple-600 flex-shrink-0"
                                  >
                                    <Sparkles size={10} />
                                    similar
                                  </span>
                                )}
                              </span>
                            </td>
                            {isAdmin && (
                              <td className="px-4 py-3 text-gray-500 truncate">
                                {rowOwner(r) || <span className="text-gray-300">—</span>}
                              </td>
                            )}
                            <td className="px-4 py-3 text-gray-500">
                              {r.product || <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-gray-500">
                              {modesLabel(r.modes) || <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-3 text-right tabular-nums text-gray-500">
                              {r.step_count ?? <span className="text-gray-300">—</span>}
                            </td>
                            {tab === "discarded" && (
                              <td className="px-4 py-3">
                                <StatusPill status={r.status || tab} />
                              </td>
                            )}
                            <td className="px-4 py-3 text-gray-400 whitespace-nowrap text-xs">
                              {fmtDate(r.created_at)}
                            </td>
                            {tab !== "discarded" && (
                              <td className="px-4 py-3 text-center">
                                {canDiscard(r) && (
                                  <button
                                    type="button"
                                    aria-label={`Discard trail: ${symptom.slice(0, 40)}`}
                                    title="Discard trail"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setConfirmDiscard({
                                        id: r.id,
                                        symptom: r.symptom_text || "(untitled)",
                                        status: r.status || tab,
                                      });
                                    }}
                                    className="inline-flex items-center justify-center rounded p-1 text-gray-400 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-all hover:bg-red-50 hover:text-red-500"
                                  >
                                    <Trash2 size={15} />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {!loading && visibleRows.length > 0 && (
                <p className="text-gray-400 text-xs mt-3">
                  {filtersActive
                    ? `${visibleRows.length} of ${rows.length} trail${
                        rows.length === 1 ? "" : "s"
                      }`
                    : `${rows.length} trail${rows.length === 1 ? "" : "s"}`}{" "}
                  · click one to review
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── discard confirmation dialog ── */}
      {confirmDiscard && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-[#253439] mb-2">
              Discard this trail?
            </h3>
            <p className="text-sm text-gray-600 mb-1 line-clamp-2">
              {confirmDiscard.symptom}
            </p>
            {confirmDiscard.status === "published" && (
              <p className="text-sm text-red-600 font-medium mt-2">
                This trail is published and currently feeds the learning
                corpus. Discarding it will remove it from retrieval.
              </p>
            )}
            {confirmDiscard.status === "submitted" && (
              <p className="text-sm text-amber-600 font-medium mt-2">
                This trail is waiting for approval. Discarding will remove it
                from the review queue.
              </p>
            )}

            {error && (
              <p className="mt-3 text-sm text-red-600">{error}</p>
            )}

            <div className="flex justify-end gap-3 mt-5">
              <button
                type="button"
                onClick={() => { setConfirmDiscard(null); setError(""); }}
                disabled={discardBusy}
                className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => handleDiscard(confirmDiscard)}
                disabled={discardBusy}
                className="px-4 py-2 text-sm font-semibold text-white bg-red-500 rounded-lg hover:bg-red-600 transition disabled:opacity-50"
              >
                {discardBusy ? "Discarding..." : "Discard"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* -- tiny status pill ------------------------------------------------ */
const STATUS_STYLES = {
  draft: "bg-gray-100 text-gray-600",
  submitted: "bg-amber-100 text-amber-800",
  published: "bg-emerald-100 text-emerald-800",
  discarded: "bg-red-50 text-red-600",
};

function StatusPill({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${cls}`}
    >
      {status}
    </span>
  );
}

export default MyTrails;
